import React, {useEffect, useState} from 'react';
import routes from '../../../../../Routes/routes';
import { getUrlParameters, TAB_INDEX } from "../../External/Model/Model";
import { REQUEST_ACCESS_STEP_MODEL } from "../Model/Model";
import {useParams} from "react-router-dom";
import {EVENT_STEP_MODEL} from "../../../Events/External/Model/Model";

/**
 * FindMySelfMainApp
 *
 * Component to handle the “Find Myself” flow.
 * Manages step navigation, button enable/disable logic, and triggers request creation.
 *
 * @author Karen Yunuen
 * @jobTitle Full Stack Developer - IBL
 * @email kpozos@ibl.mx
 *
 * @param t
 * @param {Function} navigate - Function to navigate programmatically (react-router).
 * @param {Object} steps - Object representing the steps of the QR request flow.
 * @param {Function} setSteps - Function to update the steps state.
 * @param {number} activeStep - Current active step index.
 * @param {Array} visitorList - List of selected visitors.
 * @param {Object} requestAccessItem - Request access object with details and validations.
 * @param {Function} setBtnDisabled - Function to enable/disable the Next button.
 * @param {Function} handleCreate - Function to create the request access.
 * @param {Function} navigateStep - Function to navigate to a specific step.
 * @param {Function} resetData - Function to reset all form and state data.
 *
 * @param handleCreateInReservation
 * @param setEmptyFields
 * @returns {Object} An object containing handler functions:
 * - handleClickNext: Function to navigate to the next step or trigger create request.
 * - handleNavigateBack: Function to navigate to the previous step or reset data.
 */
const FindMySelfMainApp = (
    {
        navigate,
        steps,
        setSteps,
        activeStep,
        visitorList,
        requestAccessItem,
        setBtnDisabled,
        handleCreate,
        navigateStep,
        resetData,
        handleCreateInReservation,
        email
    }
) => {
    const {eventId} = useParams();
    const stepsAux = eventId ? EVENT_STEP_MODEL.FIND_MYSELF: REQUEST_ACCESS_STEP_MODEL.FIND_MYSELF;
    const title = eventId ? 'lets_identify_you': 'request_access';
    const subtitle = eventId ? 'use_the_email_associated_with_your_invitation': '';
    const textBtnNext = eventId ? "continue" : 'next';
    const backUrl = eventId ? `${routes.eventDetails.main}${eventId}` : routes.qrRequestAccess.main;
    const [isValidEmailFindVisitor, setIsValidEmailFindVisitor] = useState(true);

    /**
     * Set initial steps when component mounts.
     *
     * @dependencies [] - Runs only once on mount.
     */
    useEffect(() => {
        setSteps({ ...stepsAux });
    }, []);

    /**
     * Enable or disable the Next button based on current step and form validation.
     *
     * @dependencies [steps, activeStep, visitorList, requestAccessItem?.requestReason]
     */
    useEffect(() => {
        switch (activeStep) {
            case TAB_INDEX.ONE:
                if(eventId){
                    if(email && isValidEmailFindVisitor)
                        setBtnDisabled(false);
                    else
                        setBtnDisabled(true);
                    return;
                }
                if (visitorList.length === 0) setBtnDisabled(true);
                else {
                    setBtnDisabled(false);
                    setIsValidEmailFindVisitor(true);
                }
                break;
            case TAB_INDEX.TWO: //No applied for eventId
                if (!requestAccessItem?.isValid()) setBtnDisabled(true);
                else setBtnDisabled(false);
                break;
            default:
                break;
        }
    }, [steps, activeStep, visitorList, requestAccessItem?.requestReason, email, isValidEmailFindVisitor]);

    /**
     * Handle the Next button click and navigate to the appropriate step.
     */
    const handleClickNext = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => eventId ? handleCreateInReservation() : navigateStep(TAB_INDEX.TWO), // Start flow with visitor selected (from ONE step)
            [TAB_INDEX.TWO]: () => navigateStep(TAB_INDEX.THREE),  //No applied for eventId // Start flow without visitor selected (from TWO step)
            [TAB_INDEX.THREE]: () => handleCreate(TAB_INDEX.FOUR), //No applied for eventId
            [TAB_INDEX.FOUR]: () => navigate(getUrlParameters({ url: routes.qrRequestAccess.main })) //No applied for eventId
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    /**
     * Handle the Back button click and navigate to the previous step or reset data.
     */
    const handleNavigateBack = () => {
        const completeSteps = {
            [TAB_INDEX.ONE]: () => navigate(getUrlParameters({ url: backUrl })),
            [TAB_INDEX.TWO]: () => { //No applied for eventId
                navigateStep(TAB_INDEX.ONE);
                resetData();
            },
            [TAB_INDEX.THREE]: () => navigateStep(TAB_INDEX.TWO), //No applied for eventId
            [TAB_INDEX.FOUR]: () => navigateStep(TAB_INDEX.THREE) //No applied for eventId
        };

        const action = completeSteps[activeStep];
        if (action) action();
    };

    return {
        handleClickNext,
        handleNavigateBack,
        title,
        subtitle,
        textBtnNext,

        isValidEmailFindVisitor,
        setIsValidEmailFindVisitor
    };
};

export default FindMySelfMainApp;
